import { Alert, Button, Group, Modal, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { rawApi } from '../../api/client';
import type { ApiError } from '../../api/errors';
import { listKey } from '../../api/queryKeys';
import {
  FILED_MONTH_REASON_FIELD,
  isFiledMonthWarning,
  WritingIntoAFiledMonth,
} from '../../components/WritingIntoAFiledMonth';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { enumLabel, enumOptions } from '../../lib/enums';
import {
  currencySymbol,
  formatDate,
  formatMoney,
  type NumberInputValue,
  toMajorUnits,
  toMinorUnits,
} from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { billSubject } from './billSubject';
import type { BillVendor } from './RecordBillForm';

const BILLS_PATH = '/admin/v1/suppliers/supplier-invoices';
const CREDITS_PATH = '/admin/v1/suppliers/supplier-credits';
const PAYABLES_PATH = '/admin/v1/suppliers/payables';
/** The owner's list of bills that do not agree with their delivery — see BillsThatDisagreeSection. */
const DISCREPANCIES_PATH = '/admin/v1/suppliers/bill-discrepancies';

/** Today, as the plain calendar date the API stores. */

/** One of a vendor's bills, with the figures the server works out. */
export interface VendorBill {
  invoice: {
    id: string;
    invoiceNumber?: string | null;
    description?: string | null;
    receiptId?: string | null;
    invoiceDate: string;
    /** Null means due on delivery, which is an answer rather than a missing one. */
    dueDate?: string | null;
    currency: string;
    totalMinor: number;
    paidMinor: number;
    status: string;
    /** What the delivery behind it came to. Null when there is no delivery to disagree with. */
    receivedTotalMinor?: number | null;
    /** Why the two disagree, in the words of whoever took the delivery. */
    discrepancyReason?: string | null;
  };
  agingDays: number;
  creditedMinor: number;
  outstandingMinor: number;
  /** Billed less what arrived. Positive: they billed for more than turned up. */
  discrepancyMinor: number;
  /** How much of that nobody has answered yet — the figure a credit note is offered for. */
  discrepancyOpenMinor: number;
}

/** Every bill this vendor has sent, newest first — the list both the credit forms choose from. */
export function useVendorBills(supplierId: string, enabled = true) {
  return useQuery({
    queryKey: listKey(BILLS_PATH, { supplierId }),
    queryFn: () => rawApi<VendorBill[]>('GET', `${BILLS_PATH}?supplierId=${supplierId}`),
    enabled: enabled && Boolean(supplierId),
  });
}

/**
 * The bills a credit can actually come off: the ones with money still on them, in the currency the
 * credit is in. A settled bill has nothing left for a credit to reduce, and offering it in the list
 * would be offering a choice the server is about to refuse.
 */
export function billsACreditCanGoAgainst(bills: VendorBill[], currency: string) {
  return bills
    .filter((b) => b.outstandingMinor > 0 && b.invoice.currency === currency)
    .map((b) => ({
      value: b.invoice.id,
      label: [
        billSubject(b.invoice),
        formatDate(b.invoice.invoiceDate),
        `${formatMoney(b.outstandingMinor, b.invoice.currency)} outstanding`,
        // Named in the list, not only once it is chosen: the bill a credit note is about is
        // almost always the one that was billed for goods that never arrived, and this is what
        // picks it out of six months of the vendor's paperwork.
        ...(b.discrepancyOpenMinor > 0
          ? [`${formatMoney(b.discrepancyOpenMinor, b.invoice.currency)} billed and not delivered`]
          : []),
      ].join(' · '),
    }));
}

/**
 * The bills a delivery of goods may be settling: the ones billed for more than turned up, in the
 * money this delivery is priced in.
 *
 * The other ending a shortfall has. They billed ten and sent seven, and the three come over on a
 * later boat with a delivery note and nothing to pay — so those goods belong on that bill's side
 * of what arrived, and no second payable is opened for beer that was charged for in May. A bill in
 * the other currency is not offered, because the server refuses it: L 1,350.00 and $1,350.00 are
 * the same digits and twenty-six times apart.
 */
export function billsGoodsWereAlreadyBilledOn(bills: VendorBill[], currency: string) {
  return bills
    .filter((b) => b.discrepancyOpenMinor > 0 && b.invoice.currency === currency)
    .map((b) => ({
      value: b.invoice.id,
      label: [
        billSubject(b.invoice),
        formatDate(b.invoice.invoiceDate),
        `${formatMoney(b.discrepancyOpenMinor, b.invoice.currency)} billed and not delivered`,
      ].join(' · '),
    }));
}

/** How much of a bill is still billed and not delivered — the most such a delivery may come to. */
export function shortfallOpenOn(bills: VendorBill[], invoiceId: string | null): number {
  if (!invoiceId) return 0;
  return bills.find((b) => b.invoice.id === invoiceId)?.discrepancyOpenMinor ?? 0;
}

/**
 * What a credit against this bill would be for, if the vendor is crediting the shortfall.
 *
 * The difference was recorded the day the delivery came in, so nobody should be reading it off a
 * second piece of paper a month later — and a figure typed twice is a figure that disagrees once.
 * It is OFFERED and never imposed: the vendor may credit something else entirely, or send the
 * missing cases instead, and a credit note nobody issued is money in the books that does not exist.
 */
function shortfallOn(bill: VendorBill | undefined): number {
  return bill && bill.discrepancyOpenMinor > 0 ? bill.discrepancyOpenMinor : 0;
}

/**
 * Writing down a credit the supplier has issued.
 *
 * The gas man reads the meter wrong and bills L2,400 for a L2,000 fill; next month he sends a
 * credit for the L400. A case of beer arrives with four bottles broken and the vendor credits them.
 * Until this existed there was nowhere to put either, and the only way to make the books agree was
 * to edit the original bill down — which loses the fact that he billed L2,400, which is the fact
 * the argument with him is about.
 *
 * ## Why this is its own button rather than a negative amount on "Record a bill"
 *
 * The same reason recording a bill is its own button and not a question on the delivery form. In a
 * data model they are one row with a sign; on the job they are not the same job. A bill is money
 * you owe and it asks when you have to pay; a credit is money coming back and it asks which bill it
 * comes off. Merging them would put a question in front of both whose answer the person knew before
 * they clicked, and it would make "How much they billed you" a box you can type a minus into.
 *
 * The amount is entered as an ordinary positive number, because that is what is printed on the
 * paper. Nobody types a minus sign, and asking them to is how a credit gets recorded as a bill.
 */
export function RecordCreditForm({
  vendor,
  againstBillId,
  onClose,
  onSaved,
}: {
  vendor: BillVendor;
  /** Set when this was opened from a bill — from the list of bills that do not agree, say. */
  againstBillId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const theirCurrency = vendor.invoiceCurrency ?? 'HNL';
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState<NumberInputValue>('');
  /** True once a person has typed in the amount box, so nothing offered ever overwrites them. */
  const [amountTouched, setAmountTouched] = useState(false);
  const [currency, setCurrency] = useState(theirCurrency);
  /**
   * The date the paper is dated, defaulted from the BAR's calendar rather than the browser's.
   *
   * `toIsoDate(new Date())` is the reader's local day. Roatán is UTC−6 and the office is often not on
   * the island, so this is the sixth place the same six-hour defect has been found: a bill typed at
   * half past seven on the last night of a month was dated the first of the next one, and dropped out
   * of that month's ISV return. `useIslandToday` carries the reasoning.
   */
  const islandToday = useIslandToday();
  const [creditDate, setCreditDate] = useState(islandToday);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [againstId, setAgainstId] = useState<string | null>(againstBillId ?? null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Set when the credit note is dated in a month the accountant has already filed. A credit moves
   * that month's spend exactly as a bill does — downwards — so it is the same question, asked the
   * same way, with the form left open behind it.
   */
  const [filedMonthWarning, setFiledMonthWarning] = useState<ApiError | null>(null);

  const bills = useVendorBills(vendor.id);
  const choices = billsACreditCanGoAgainst(bills.data ?? [], currency);
  const against = (bills.data ?? []).find((b) => b.invoice.id === againstId);
  /*
   * THE FIGURE IS OFFERED, NOT ASKED FOR.
   *
   * The shortfall was written down the day the delivery arrived — three cases of Salva Vida, L
   * 1,350.00 — so retyping it off the credit note a month later is asking for the one number this
   * screen already knows, and asking twice is how two numbers come to disagree. It fills in only
   * while nobody has typed, and the box stays fully editable, because the credit the vendor
   * actually issued is whatever is printed on their paper and not what we expected.
   */
  const offered = shortfallOn(against);
  useEffect(() => {
    if (offered > 0 && !amountTouched) setAmount(toMajorUnits(offered));
  }, [offered, amountTouched]);

  const amountMinor = toMinorUnits(amount) ?? 0;
  // A credit never takes a bill below nothing owed — the server refuses it, and so does this, in
  // the same words, beside the box that caused it.
  const tooBig = Boolean(against) && amountMinor > (against?.outstandingMinor ?? 0);

  const save = useMutation({
    mutationFn: (input: { filedMonthReason?: string } = {}) =>
      rawApi('POST', CREDITS_PATH, {
        supplierId: vendor.id,
        description: description.trim(),
        amountMinor,
        currency,
        creditDate,
        referencesInvoiceId: againstId,
        invoiceNumber: invoiceNumber.trim() || null,
        // Attached only once the server has asked for it — see the dialog for why never before.
        ...(input.filedMonthReason ? { [FILED_MONTH_REASON_FIELD]: input.filedMonthReason } : {}),
      }),
    onSuccess: () => {
      setFiledMonthWarning(null);
      onSaved();
      onClose();
      notifyDone(
        'Credit recorded',
        against
          ? `${formatMoney(amountMinor, currency)} off what you owe ${vendor.name} on that bill.`
          : `${formatMoney(amountMinor, currency)} sitting with ${vendor.name}. Put it against a bill when their next one arrives.`,
      );
    },
    onError: (err: Error) => {
      if (isFiledMonthWarning(err)) return setFiledMonthWarning(err);
      notifyError(err, 'record this credit');
    },
  });

  const submit = () => {
    if (!description.trim()) return setError('Say what the credit was for');
    if (amountMinor <= 0) return setError('Say how much they credited you');
    if (!creditDate) return setError('Say what date is on the credit note');
    if (tooBig) return setError('That is more than is still owed on the bill you picked');
    setError(null);
    save.mutate({});
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={`Record a credit from ${vendor.name}`}
      size="lg"
      // Half-completed forms are lost to a stray click otherwise.
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Money coming back the other way — an over-charge they corrected, goods that arrived broken, a credit
          for goodwill. It comes off what you owe them and off what you have spent with them. It moves no
          stock: goods that went back are a matter for the delivery they came on.
        </Text>

        <TextInput
          label="What the credit was for"
          description="In the words on the paper, so this row can be checked against it later"
          placeholder="Over-read the propane meter"
          required
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          disabled={save.isPending}
        />

        <Group grow align="flex-start">
          <NumberInput
            label="How much they credited you"
            required
            min={0}
            prefix={`${currencySymbol(currency)} `}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator=","
            hideControls
            description={
              offered > 0
                ? `${formatMoney(offered, currency)} was billed and not delivered on that bill. Change it if their credit note says otherwise.`
                : undefined
            }
            value={amount}
            // Kept exactly as the box hands it back — see `NumberInputValue` in lib/format.
            onChange={(v) => {
              setAmountTouched(true);
              setAmount(v);
            }}
            disabled={save.isPending}
          />
          <Select
            label="Credited in"
            description={`${vendor.name} normally bills in ${enumLabel('currency', theirCurrency)}`}
            required
            data={enumOptions('currency')}
            value={currency}
            onChange={(v) => {
              setCurrency(v ?? theirCurrency);
              // The bill it was going against may be in the other money, and a credit and a bill in
              // different currencies is twenty-six times apart. Cleared rather than left to fail.
              setAgainstId(null);
            }}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
            disabled={save.isPending}
          />
        </Group>

        {/* The one question that makes this a credit rather than a number: which bill does it come
            off. "Nothing in particular" is a real answer here and is the default only in the sense
            that it is what an empty box means — a vendor issuing a goodwill credit has no invoice to
            point at, and inventing one would be a bill that never existed. */}
        <Select
          label="Which bill it comes off"
          description={
            choices.length === 0
              ? `Nothing of theirs is outstanding in ${enumLabel('currency', currency)}. Record it as a credit sitting with them and put it against their next bill.`
              : 'Leave it blank if it is not against a particular bill — you can put it against one later'
          }
          placeholder={bills.isLoading ? 'Loading…' : 'Not against a particular bill'}
          data={choices}
          value={againstId}
          onChange={setAgainstId}
          clearable
          searchable
          comboboxProps={{ withinPortal: true }}
          disabled={save.isPending || choices.length === 0}
        />

        <Group grow align="flex-start">
          <DateInput
            label="Dated"
            description="The date on the credit note, not the day you are typing it"
            required
            value={creditDate || null}
            onChange={(v) => setCreditDate(v ?? '')}
            valueFormat="D MMM YYYY"
            popoverProps={{ withinPortal: true }}
            disabled={save.isPending}
          />
          <TextInput
            label="Credit note number"
            description="If they gave you one"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.currentTarget.value)}
            disabled={save.isPending}
          />
        </Group>

        {tooBig && against && (
          <Alert color="red" variant="light" title="That is more than is left on that bill">
            {formatMoney(against.outstandingMinor, against.invoice.currency)} is still owed on it. A credit
            cannot take a bill below nothing owed — record the rest as a credit sitting with them.
          </Alert>
        )}

        {currency !== theirCurrency && (
          <Alert color="yellow" variant="light" title="That is not the money they usually bill in">
            {vendor.name} normally bills in {enumLabel('currency', theirCurrency)}, and this credit is in{' '}
            {enumLabel('currency', currency)}. It can only come off a bill in{' '}
            {enumLabel('currency', currency)}.
          </Alert>
        )}

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={submit} disabled={tooBig}>
            Record the credit
          </Button>
        </Group>
      </Stack>
      <WritingIntoAFiledMonth
        warning={filedMonthWarning}
        saving={save.isPending}
        onCancel={() => setFiledMonthWarning(null)}
        onConfirm={(filedMonthReason) => save.mutate({ filedMonthReason })}
      />
    </Modal>
  );
}

/**
 * The button that starts a credit from the vendor you are already looking at.
 *
 * Beside "Record a bill" for the same reason that one is beside the Bills heading: the vendor is
 * already known, and a credit belongs on the list of bills it is about to change.
 */
export function RecordCreditButton({
  vendor,
  againstBillId,
  label = 'Record a credit',
}: {
  vendor: BillVendor;
  /** Set when the button sits on one bill — the shortfall on it is then the figure offered. */
  againstBillId?: string;
  label?: string;
}) {
  const queryClient = useQueryClient();
  const [recording, setRecording] = useState(false);
  return (
    <>
      <Button size="compact-sm" variant="default" onClick={() => setRecording(true)}>
        {label}
      </Button>
      {recording && (
        <RecordCreditForm
          vendor={vendor}
          againstBillId={againstBillId}
          onClose={() => setRecording(false)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: listKey(BILLS_PATH, { supplierId: vendor.id }) });
            void queryClient.invalidateQueries({ queryKey: listKey(PAYABLES_PATH) });
            // A credit that covers the shortfall takes the bill off the disagreement list, and a
            // list still showing it is a list the owner acts on twice.
            void queryClient.invalidateQueries({ queryKey: listKey(DISCREPANCIES_PATH) });
          }}
        />
      )}
    </>
  );
}

/**
 * Spending a credit that was standing on its own.
 *
 * Without this a goodwill credit is a dead end: it sits against the vendor for ever while their
 * next bill shows as owed in full and the vendor deducts the credit from it anyway — which is the
 * mismatch that gets found on a statement six months later. It is the same act as issuing a credit
 * against a bill, happening later, so it is held to the same rules.
 */
export function ApplyCreditForm({
  vendor,
  credit,
  onClose,
  onSaved,
}: {
  vendor: BillVendor;
  /** The standing credit, with its amount already positive. */
  credit: { id: string; amountMinor: number; currency: string; subject: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [billId, setBillId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bills = useVendorBills(vendor.id);
  const choices = billsACreditCanGoAgainst(bills.data ?? [], credit.currency);
  const chosen = (bills.data ?? []).find((b) => b.invoice.id === billId);
  const tooBig = Boolean(chosen) && credit.amountMinor > (chosen?.outstandingMinor ?? 0);

  const apply = useMutation({
    mutationFn: () => rawApi('POST', `${CREDITS_PATH}/${credit.id}/apply`, { supplierInvoiceId: billId }),
    onSuccess: () => {
      onSaved();
      onClose();
      notifyDone(
        'Credit put against a bill',
        `${formatMoney(credit.amountMinor, credit.currency)} off what you owe ${vendor.name} on that bill.`,
      );
    },
    onError: (err: Error) => notifyError(err, 'put this credit against a bill'),
  });

  const submit = () => {
    if (!billId) return setError('Pick the bill it comes off');
    if (tooBig) return setError('That credit is more than is still owed on the bill you picked');
    setError(null);
    apply.mutate();
  };

  return (
    <Modal opened onClose={onClose} title="Put this credit against a bill" size="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {formatMoney(credit.amountMinor, credit.currency)} from {vendor.name} — {credit.subject}. Pick the
          bill it comes off and it stops being owed.
        </Text>

        <Select
          label="Which bill"
          description={
            choices.length === 0
              ? `Nothing of theirs is outstanding in ${enumLabel('currency', credit.currency)}. The credit keeps until one is.`
              : 'Only bills with money still on them, in the money the credit is in'
          }
          placeholder={bills.isLoading ? 'Loading…' : 'Pick a bill'}
          data={choices}
          value={billId}
          onChange={setBillId}
          searchable
          comboboxProps={{ withinPortal: true }}
          disabled={apply.isPending || choices.length === 0}
        />

        {tooBig && chosen && (
          <Alert color="red" variant="light" title="That is more than is left on that bill">
            {formatMoney(chosen.outstandingMinor, chosen.invoice.currency)} is still owed on it, and this
            credit is {formatMoney(credit.amountMinor, credit.currency)}. A credit cannot take a bill below
            nothing owed.
          </Alert>
        )}

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={apply.isPending}>
            Cancel
          </Button>
          <Button loading={apply.isPending} onClick={submit} disabled={!billId || tooBig}>
            Put it against that bill
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
